import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  message,
  Modal,
  Form,
  Tag,
  Tooltip,
  DatePicker,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../../components/common/DataTable";
import usePermission from "../../../hooks/usePermission";
import useSettingsDropdown from "../../../hooks/useSettingsDropdown";
import ConfirmDeleteModal from "../../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../../components/common/DeletedTag";
import {
  listPendingLeaves,
  listApprovedLeaves,
  listRejectedLeaves,
  approveLeave,
  rejectLeave,
  bulkApproveLeaves,
  deleteLeave,
  exportLeaves,
  downloadLeaveAttachment,
} from "../../../services/leaves.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const STATUS_COLORS = {
  pending: { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  approved: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
};

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Tag
      style={{
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        textTransform: "capitalize",
        fontWeight: 600,
      }}
    >
      {status}
    </Tag>
  );
}

function formatDate(value) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD");
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Shared Leave Management tab for Pending / Approved / Rejected.
 * @param {'pending'|'approved'|'rejected'} status
 */
export default function LeaveTab({ status }) {
  const { canApprove, canDelete } = usePermission("usersLeaves");
  const { options: leaveTypeOptions } = useSettingsDropdown("leave-types");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [leaveType, setLeaveType] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [month, setMonth] = useState(undefined);
  const [year, setYear] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [rejectModal, setRejectModal] = useState({ open: false, record: null });
  const [rejectForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search || undefined,
        leaveType: leaveType || undefined,
      };

      if (status === "pending" && dateRange?.[0] && dateRange?.[1]) {
        params.fromDate = dateRange[0].startOf("day").toISOString();
        params.toDate = dateRange[1].endOf("day").toISOString();
      }

      if ((status === "approved" || status === "rejected") && (month || year)) {
        if (month) params.month = month;
        if (year) params.year = year;
      }

      const listFn =
        status === "pending"
          ? listPendingLeaves
          : status === "approved"
            ? listApprovedLeaves
            : listRejectedLeaves;

      const response = await listFn(params);
      setRows(response?.data?.records || []);
      setTotalCount(response?.data?.pagination?.total_count || 0);
      setSelectedRowKeys([]);
    } catch {
      message.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, leaveType, dateRange, month, year, status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleApprove = async (record) => {
    try {
      setActionLoading(true);
      await approveLeave(record._id);
      message.success("Leave approved");
      fetchList();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openReject = (record) => {
    rejectForm.resetFields();
    setRejectModal({ open: true, record });
  };

  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      setActionLoading(true);
      await rejectLeave(rejectModal.record._id, values.rejectionReason);
      message.success("Leave rejected");
      setRejectModal({ open: false, record: null });
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.errors?.[0]?.msg || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = () => {
    if (!selectedRowKeys.length) {
      message.warning("Select at least one leave request");
      return;
    }
    Modal.confirm({
      title: "Bulk Approve",
      content: `Approve ${selectedRowKeys.length} selected leave request(s)?`,
      okText: "Approve All",
      onOk: async () => {
        try {
          const res = await bulkApproveLeaves(selectedRowKeys);
          message.success(res?.data?.msg || "Bulk approve completed");
          fetchList();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Bulk approve failed");
        }
      },
    });
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Approved Leave",
      content: `Soft-delete leave for ${record.partnerName || "this partner"}?`,
      onConfirm: async () => {
        try {
          await deleteLeave(record._id);
          message.success("Leave deleted");
          fetchList();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Delete failed");
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const raw = await exportLeaves({
        status,
        search: search || undefined,
        leaveType: leaveType || undefined,
        month: month || undefined,
        year: year || undefined,
      });
      const blob =
        raw instanceof Blob
          ? raw
          : new Blob([raw], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `Leaves_${status}_${dayjs().format("YYYY-MM-DD")}.csv`);
      message.success("Export downloaded");
    } catch {
      message.error("Export failed");
    }
  };

  const handleAttachment = async (record) => {
    if (!record.hasAttachment) {
      message.info("No attachment");
      return;
    }
    try {
      const blob = await downloadLeaveAttachment(record._id);
      downloadBlob(blob, record.attachmentFileName || "leave-attachment");
    } catch {
      message.error("Failed to download attachment");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: formatDate,
    },
    {
      title: "Partner ID",
      dataIndex: "partnerId",
      key: "partnerId",
      width: 130,
      render: (v, record) => (
        <span style={{ fontFamily: "monospace" }}>
          <PartnerLabel name={v || "—"} deleted={record.riderDeleted} />
        </span>
      ),
    },
    {
      title: "Partner Name",
      dataIndex: "partnerName",
      key: "partnerName",
      width: 180,
      render: (v, record) => (
        <strong>
          <PartnerLabel name={v} deleted={record.riderDeleted} />
        </strong>
      ),
    },
    {
      title: "Leave Type",
      dataIndex: "leaveTypeName",
      key: "leaveTypeName",
      width: 130,
    },
    {
      title: "From",
      dataIndex: "fromDate",
      key: "fromDate",
      width: 110,
      render: formatDate,
    },
    {
      title: "To",
      dataIndex: "toDate",
      key: "toDate",
      width: 110,
      render: formatDate,
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <StatusBadge status={s} />,
    },
    {
      title: "Attachment",
      key: "attachment",
      width: 100,
      render: (_, record) =>
        record.hasAttachment ? (
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            onClick={() => handleAttachment(record)}
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      width: 180,
    },
  ];

  if (status === "approved") {
    columns.push(
      {
        title: "Approved By",
        dataIndex: "approvedByName",
        key: "approvedByName",
        width: 140,
      },
      {
        title: "Action Date",
        dataIndex: "actionDate",
        key: "actionDate",
        width: 110,
        render: formatDate,
      }
    );
  }

  if (status === "rejected") {
    columns.push(
      {
        title: "Rejected By",
        dataIndex: "rejectedByName",
        key: "rejectedByName",
        width: 140,
      },
      {
        title: "Action Date",
        dataIndex: "actionDate",
        key: "actionDate",
        width: 110,
        render: formatDate,
      },
      {
        title: "Rejection Reason",
        dataIndex: "rejectionReason",
        key: "rejectionReason",
        width: 200,
        render: (v) => (
          <span style={{ color: "#b91c1c", fontWeight: 500 }}>{v || "—"}</span>
        ),
      }
    );
  }

  if (status === "pending") {
    columns.push({
      title: "Action",
      key: "actions",
      fixed: "right",
      width: 160,
      render: (_, record) =>
        canApprove ? (
          <Space size="small">
            <Tooltip title="Approve">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={actionLoading}
                onClick={() => handleApprove(record)}
                style={{ background: "#111827" }}
              />
            </Tooltip>
            <Tooltip title="Reject">
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => openReject(record)}
              />
            </Tooltip>
          </Space>
        ) : (
          "—"
        ),
    });
  }

  if (status === "approved" && canDelete) {
    columns.push({
      title: "Action",
      key: "delete",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Tooltip>
      ),
    });
  }

  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const y = dayjs().year() - i;
    return { label: String(y), value: y };
  });

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    label: dayjs().month(i).format("MMMM"),
    value: i + 1,
  }));

  return (
    <div>
      <div
        style={{
          background: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: 10,
          padding: 16,
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 220px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Search Partner
          </div>
          <Input.Search
            placeholder="Partner ID or Name"
            allowClear
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
          />
        </div>

        <div style={{ width: 180 }}>
          <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
            Leave Type
          </div>
          <Select
            allowClear
            placeholder="All Types"
            style={{ width: "100%" }}
            options={leaveTypeOptions}
            value={leaveType}
            onChange={(v) => {
              setPage(1);
              setLeaveType(v);
            }}
          />
        </div>

        {status === "pending" && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
              Date Range
            </div>
            <RangePicker
              value={dateRange}
              onChange={(v) => {
                setPage(1);
                setDateRange(v);
              }}
            />
          </div>
        )}

        {(status === "approved" || status === "rejected") && (
          <>
            <div style={{ width: 140 }}>
              <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
                Month
              </div>
              <Select
                allowClear
                placeholder="Month"
                style={{ width: "100%" }}
                options={monthOptions}
                value={month}
                onChange={(v) => {
                  setPage(1);
                  setMonth(v);
                }}
              />
            </div>
            <div style={{ width: 120 }}>
              <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>
                Year
              </div>
              <Select
                allowClear
                placeholder="Year"
                style={{ width: "100%" }}
                options={yearOptions}
                value={year}
                onChange={(v) => {
                  setPage(1);
                  setYear(v);
                }}
              />
            </div>
          </>
        )}

        <Button icon={<ReloadOutlined />} onClick={fetchList}>
          Refresh
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
        {status === "pending" && canApprove && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={!selectedRowKeys.length}
            onClick={handleBulkApprove}
            style={
              selectedRowKeys.length
                ? { background: "#111827", borderColor: "#111827" }
                : undefined
            }
          >
            Bulk Approve
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={(p) => setPage(p)}
        scroll={{ x: 1200 }}
        rowSelection={
          status === "pending" && canApprove
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
            : undefined
        }
      />

      <Modal
        title="Reject Leave Request"
        open={rejectModal.open}
        onCancel={() => setRejectModal({ open: false, record: null })}
        onOk={handleRejectSubmit}
        confirmLoading={actionLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[
              { required: true, message: "Rejection reason is required" },
              { min: 3, message: "At least 3 characters" },
            ]}
          >
            <TextArea rows={4} placeholder="Explain why this leave is being rejected..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
